import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeGetCellValue, selectSeqNum } from '../../../redux/globalsSlice';
import { 
   EVENT_TYPE_AMEND_ORDER, JSON_KEY_ORDER_NUMBER, ORDER_TYPE_LIMIT, DURATION_IMMEDIATE, DURATION_DAY, DURATION_GTC,
   GetMinStep, SELL_SIDE, Get10Power, CODE_HINT, ACCOUNT_HINT, PRICE_DECIMALS, QTY_DECIMALS, ShowError, confirmationDialog,
   JSON_KEY_INSTRUMENT, JSON_KEY_TRADING_ACCOUNT, JSON_KEY_SPECIAL_TYPE, JSON_KEY_DURATION, 
   JSON_KEY_ORDER_TYPE, JSON_KEY_PRICE, JSON_KEY_VISIBLE_QTY, JSON_KEY_IN_SEQ, JSON_KEY_USER, 
   EVENT_TOPIC_FOR_TRANSACTION_SERVER, JSON_KEY_MESSAGE_TYPE, JSON_KEY_ORIGINAL_QTY,
   ConvertToOrderType,
   HIDDEN,
   FOK,
   IMMEDIATE,
   DROPDOWN_LIST_NONE,
   DROPDOWN_LIST_NONE_ID,
   TRIGGER_PRICE_LESS_THAN_OR_EQUAL_TO_BID_PRICE,
   JSON_KEY_TRIGGER_PRICE,
   JSON_KEY_TRIGGER_CONDITION,
   JSON_KEY_ORDER_TYPE_FLAGS,
   JSON_KEY_SESSION_TYPE,
   TRIGGER_FLAG,
   SCHEDULE_FLAG,
   SESSION_TYPE_NOT_SET,
   JSON_KEY_TRIGGER_DURATION,
} from '../../../common/common';
import { DispatchTransactionEvent } from '../../events/events';
import { useForm } from 'react-hook-form';
import { MSGTYPE_ORDER_AMEND } from '../../../common/msg_types';

export function OrderAmendLogic(onClose) {
   const { register, handleSubmit, } = useForm();

   const [orderNumber, setOrderNumber] = useState('');
   const [code, setCode] = useState('');
   const [tradingAccount, setTradingAccount] = useState('');
   const [verb, setVerb] = useState('');
   const [price, setPrice] = useState('');
   const [qty, setQty] = useState('');
   const [visible_qty, setVisibleQty] = useState('');
   const [visible_balance, setVisibleBalance] = useState('');
   const [total_balance, setTotalBalance] = useState('');
   const [orderType, setOrderType] = useState(ORDER_TYPE_LIMIT);
   const [fok, setFOK] = useState(false);
   const [hidden, setHidden] = useState(false);
   const [durationType, setDurationType] = useState(DURATION_DAY);
   const [triggerDurationType, setTriggerDurationType] = useState(DURATION_DAY);
   const [specialType, setSpecialType] = useState(DROPDOWN_LIST_NONE_ID);
   const [session, setSession] = useState(SESSION_TYPE_NOT_SET);
   const [buySell, setBuySell] = useState(SELL_SIDE);
   const [qty_step, setQtyStep] = useState(1);
   const [price_step, setPriceStep] = useState(0.01);
   const getDecimalValue = useMemo(() => makeGetCellValue(), []);
   const price_dec = useSelector((state) => getDecimalValue(state, code, PRICE_DECIMALS));
   const qty_dec = useSelector((state) => getDecimalValue(state, code, QTY_DECIMALS));
   const [userId, setUserId]= useState('');
   const [triggerCondition, setTriggerCondition] = useState(TRIGGER_PRICE_LESS_THAN_OR_EQUAL_TO_BID_PRICE);
   const [triggerPrice, setTriggerPrice] = useState('');
   const [scheduleOrder, setScheduleOrder] = useState(false);
   const [triggerOrder, setTriggerOrder] = useState(false);

   useEffect(() => {
      if (price_dec !== undefined) {
        setPriceStep(GetMinStep(price_dec));
      }
    }, [price_dec]);
    
   useEffect(() => {
      if (qty_dec !== undefined) {
         setQtyStep(GetMinStep(qty_dec));
      }
   }, [qty_dec]);

   const inSeqNum = useSelector(selectSeqNum);
   const inSeqNumRef = useRef(inSeqNum);

   useEffect(() => {       // to get the latest seqnum
      inSeqNumRef.current = inSeqNum;
   }, [inSeqNum]);

   const isValid = Number(qty) > 0 && Number(price) > 0;

   const onSubmit = async () => {
      // console.log('Form submitted, code:', code, ', tradingAccount:', tradingAccount, ', price:', price, ', qty:', qty, ', visible_qty:', visible_qty, ', orderType:', orderType, ', fok:', fok, ', hidden:', hidden, ', durationType:', durationType, ', buySell:', buySell);
      if ((code === CODE_HINT) || (code === '')) {
         ShowError("Instrument Code is missing");
         return;      
      }

      if ((tradingAccount === ACCOUNT_HINT) || (tradingAccount === '')) {
         ShowError("Account is missing");
         return;      
      }

      if (orderType === ORDER_TYPE_LIMIT) {
        if (price === '') {
            ShowError("Limit order must have a price");
            return;
         }

         if (qty === '') {
            ShowError("Quantity is missing");
            return;
         }

         if (hidden) {
            console.log('hidden: ', hidden);
            if (visible_qty === '') {
               ShowError("Hidden order is missing visible quantity");
               return;
            }

            if (parseFloat(visible_qty) >= parseFloat(qty)) {
               ShowError("Visible quantity must be less than quantity");
               return;
            }
         }
      }

      if (fok && durationType !== IMMEDIATE) {
         ShowError("FOK must have duration of immediate");
      }

      // trigger order checks      
      if (triggerOrder) {
         if (!triggerPrice || triggerPrice === '') {
               ShowError("Trigger order is missing trigger price");
               return;
         }
         
         if (parseFloat(triggerPrice) <= 0) {
               ShowError("Trigger price must be greater than zero");
               return;
         }

         if (!triggerCondition || triggerCondition === '' || triggerCondition === DROPDOWN_LIST_NONE) {
            ShowError("Missing trigger condition, ", triggerCondition);
            return;
         }
      }

      // session order checks
      if (scheduleOrder) {
         if (!session || session === '' || session === SESSION_TYPE_NOT_SET) {
            ShowError("Please select a session type (e.g., Preopen, Open, Close, Auction)");
            return;
         }         
      }

      const rounderPrice = Math.round(price * Get10Power(price_dec));
      const roundQuantity = Math.round(qty * Get10Power(qty_dec));
      const roundVisibleQty = Math.round(visible_qty * Get10Power(qty_dec));
      // const roundTotalBalance = Math.round(total_balance * Get10Power(qty_dec));
      
      const amended_order = {
         [JSON_KEY_MESSAGE_TYPE]: MSGTYPE_ORDER_AMEND,
         [JSON_KEY_ORDER_NUMBER]: orderNumber,
         [JSON_KEY_INSTRUMENT]: code,
         [JSON_KEY_TRADING_ACCOUNT]: tradingAccount,
         [JSON_KEY_DURATION]: durationType,    
         [JSON_KEY_ORDER_TYPE]: ConvertToOrderType(orderType),
         [JSON_KEY_PRICE]: rounderPrice,
         [JSON_KEY_ORIGINAL_QTY]: roundQuantity,
         [JSON_KEY_VISIBLE_QTY]: roundVisibleQty,
         [JSON_KEY_IN_SEQ]: Number(inSeqNumRef.current),
         [JSON_KEY_USER]: userId,
         // [JSON_KEY_TOTAL_BALANCE]: roundTotalBalance,

      };

      if (fok) {
         amended_order[JSON_KEY_SPECIAL_TYPE] = FOK;
      }
      else if (hidden) {
         amended_order[JSON_KEY_SPECIAL_TYPE] = HIDDEN;
      }

      if (triggerOrder) {
         const rounderTriggerPrice = Math.round(triggerPrice * Get10Power(price_dec));
         amended_order[JSON_KEY_TRIGGER_PRICE] = rounderTriggerPrice;
         amended_order[JSON_KEY_TRIGGER_CONDITION] = triggerCondition;
         amended_order[JSON_KEY_TRIGGER_DURATION] = triggerDurationType;
         amended_order[JSON_KEY_ORDER_TYPE_FLAGS] = TRIGGER_FLAG;
      }

      if (scheduleOrder) {
         amended_order[JSON_KEY_SESSION_TYPE] = session;
         amended_order[JSON_KEY_ORDER_TYPE_FLAGS] = (amended_order[JSON_KEY_ORDER_TYPE_FLAGS] || 0) | SCHEDULE_FLAG;
      }
      
      // build confirmation message
      let message = '';
      // console.log('orderType: ', orderType);
      message += orderType;

      if (hidden) message += ', Hidden';

      if (fok) message += ' ORDER_TYPE_FOK';
      
      message += ' order';

      if (orderType === ORDER_TYPE_LIMIT) {
         message += ' with price ' + price;
      }

      message += ' , quantity ' + qty;

      if (hidden) {
         message += ' , visible quantity ' + visible_qty;
      }
      
      switch (durationType) {
         case DURATION_IMMEDIATE:
            message += ' and duration Immediate';
            break;
         case DURATION_DAY:
            message += ' and duration Day';
            break;
         case DURATION_GTC:
            message += ' and duration GTC';
            break;   
         default:
            message += ' and duration Unknown';
            break;
      }
      
      // confirmation check
      const confirmed = await confirmationDialog({
         title: code + (buySell === SELL_SIDE ? ' Sell Order Modified' : ' Buy Order Modified'),
         text: message,
      });

      if (confirmed) {
         // console.log('Submitting order amend:', amended_order);
         DispatchTransactionEvent(EVENT_TOPIC_FOR_TRANSACTION_SERVER, EVENT_TYPE_AMEND_ORDER, amended_order); 
         onClose();
      }
   };

   return {
      orderNumber, setOrderNumber, tradingAccount, setTradingAccount, verb, setVerb,
      orderType, hidden, fok, durationType, code,
      triggerDurationType, setTriggerDurationType,
      setOrderType, setHidden, setFOK, setDurationType, setCode,
      price, price_dec, 
      qty, qty_dec,
      visible_qty, setVisibleQty,
      visible_balance, setVisibleBalance, 
      triggerCondition, setTriggerCondition,
      triggerPrice, setTriggerPrice,
      specialType, setSpecialType,
      session, setSession,
      total_balance, setTotalBalance, 
      buySell, setBuySell,
      qty_step, setQtyStep, price_step, setPriceStep,
      setPrice,
      setQty,
      isValid, setUserId,
      handleSubmit, onSubmit, register,
      scheduleOrder, setScheduleOrder,
      triggerOrder, setTriggerOrder,
   };
}

